import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { catchError, combineLatest, debounceTime, distinctUntilChanged, finalize, from, map, Observable, of, startWith, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';

import { naturalCompare } from '@shared/functions/natural-sort';
import { toSqlDateTime } from '@core/functions/to-date-string';
import { TranslationModule } from '@core/translation/translation.module';
import { BerthApiService } from '@bcmApiServices/berth.api-service';
import { BoatApiService } from '@bcmApiServices/boat.api-service';
import { CompanyApiService } from '@bcmApiServices/company.api-service';
import { PersonApiService } from '@bcmApiServices/person.api-service';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import { SelectionSummaryComponent } from '../../components/selection-summary/selection-summary.component';
import { BookingV2DimensionsComponent } from '../../components/dimensions/booking-v2-dimensions.component';
import {
    BookingV2BerthOccupancy,
    BookingV2BerthOption,
    BookingV2BoatDimensions,
    BookingV2BoatOption,
    BookingV2BoatOrigin,
    BookingV2OwnerOption,
} from '../../dtos/booking-v2.dtos';
import { BookingV2Facade } from '../../services/booking-v2.facade';
import { isSameBoatSelection } from '../../utils/booking-v2-boat-identity';
import { isUp2boatId, Up2boatIdSelectionService } from '@modules/bcm/@shared/services/up2boat-id-selection.service';
import {
    BOOKING_V2_MIN_SEARCH_LENGTH,
    isBookingV2SearchTooShort,
} from '../../utils/booking-v2-search-query.util';
import { toBerthOption, toBoatOption, toCompanyOwner, toManualBoatOption, toPersonOwner } from '../../utils/booking-v2-option.mappers';

export type BerthSelectionMode = 'existingBoat' | 'properties' | 'direct' | 'newBoat' | null;

interface ModeOption {
    mode: Exclude<BerthSelectionMode, null>;
    icon: string;
    titleKey: string;
    descKey: string;
}

interface BerthLoadRequest {
    from: Date;
    /** `null` = openEnd; the API receives no `to` param. */
    to: Date | null;
}

interface BoatSearchResult {
    boats: readonly BookingV2BoatOption[];
    owners: readonly BookingV2OwnerOption[];
    error: boolean;
}

// A berth row plus its fit verdict against the fit basis (`null` = fits / no basis), so the
// template renders the badge without re-evaluating fit per change-detection pass.
// `boatUnfitReason` is the verdict against the selected boat while the list is fitted to
// search dimensions beside it (7165), else `null`.
interface BerthListItem {
    berth: BookingV2BerthOption;
    unfitReason: BerthUnfitReason | null;
    boatUnfitReason: BerthUnfitReason | null;
}

const MODE_OPTIONS: readonly ModeOption[] = [
    { mode: 'existingBoat', icon: 'search',     titleKey: 'bookingV2.berth.modes.existingBoat.title', descKey: 'bookingV2.berth.modes.existingBoat.desc' },
    { mode: 'properties',   icon: 'straighten', titleKey: 'bookingV2.berth.modes.properties.title',   descKey: 'bookingV2.berth.modes.properties.desc' },
    { mode: 'direct',       icon: 'anchor',     titleKey: 'bookingV2.berth.modes.direct.title',       descKey: 'bookingV2.berth.modes.direct.desc' },
    { mode: 'newBoat',      icon: 'add',        titleKey: 'bookingV2.berth.modes.newBoat.title',      descKey: 'bookingV2.berth.modes.newBoat.desc' },
];

const MIN_SEARCH_LENGTH = BOOKING_V2_MIN_SEARCH_LENGTH;
const SEARCH_DEBOUNCE_MS = 300;

@Component({
    selector: 'booking-v2-berth-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        Checkbox,
        IconField,
        InputIcon,
        InputText,
        ProgressSpinner,
        Tooltip,
        TranslationModule,
        U2bIconButtonComponent,
        SelectionSummaryComponent,
        BookingV2DimensionsComponent,
    ],
    templateUrl: './berth-step.component.html',
    host: { class: 'block' },
})
export class BerthStepComponent {

    private readonly facade = inject(BookingV2Facade);
    private readonly berthApi = inject(BerthApiService);
    private readonly boatApi = inject(BoatApiService);
    private readonly personApi = inject(PersonApiService);
    private readonly companyApi = inject(CompanyApiService);
    private readonly up2boatIds = inject(Up2boatIdSelectionService);

    readonly modeOptions = MODE_OPTIONS;

    readonly selectionMode = signal<BerthSelectionMode>(null);
    readonly multiSelect = signal<boolean>(false);
    readonly berthSearch = signal<string>('');

    // When on, berths the boat does not fit are appended (marked, still selectable)
    // instead of being filtered out. Only meaningful with a boat in the selection.
    readonly showUnfitting = signal<boolean>(false);

    // Client-side width add-on; not persisted, only used for berth filtering.
    readonly useFender = signal<boolean>(false);
    readonly fenderCm = signal<number>(30);

    // Properties mode — the search form inputs. Kept across mode switches while applied.
    readonly propLength = signal<number | null>(null);
    readonly propWidth = signal<number | null>(null);
    readonly propDepth = signal<number | null>(null);

    readonly propertiesValid = computed(() =>
        positiveOrZero(this.propLength()) > 0 &&
        positiveOrZero(this.propWidth()) > 0 &&
        positiveOrZero(this.propDepth()) > 0,
    );

    // The applied properties search. It filters the berth list while that panel is open and
    // never replaces a selected boat (7165); only without one does applying it also create the
    // dimension-only placeholder boat. Dropped whenever the boat changes.
    private readonly searchDims = signal<BookingV2BoatDimensions | null>(null);
    private readonly activeSearchDims = computed<BookingV2BoatDimensions | null>(() =>
        this.selectionMode() === 'properties' ? this.searchDims() : null,
    );

    // The active search shown beside a real/new boat in the summary — a placeholder boat
    // already displays these dimensions as its own.
    readonly searchDimsBesideBoat = computed<BookingV2BoatDimensions | null>(() => {
        const boat = this.facade.selectedBoat();
        return boat && boat.origin !== 'manual' ? this.activeSearchDims() : null;
    });

    // New-boat mode — draft data to be persisted at booking commit.
    readonly newBoatName = signal<string>('');
    readonly newBoatLicense = signal<string>('');
    readonly newBoatLength = signal<number | null>(null);
    readonly newBoatWidth = signal<number | null>(null);
    readonly newBoatDepth = signal<number | null>(null);

    // Optional owner for the new boat — an existing person/company.
    readonly newBoatOwner = signal<{ id: number; kind: 'person' | 'company'; fullName: string } | null>(null);
    readonly ownerQuery = signal<string>('');
    readonly loadingOwnerSearch = signal<boolean>(false);

    readonly newBoatValid = computed(() =>
        positiveOrZero(this.newBoatLength()) > 0 &&
        positiveOrZero(this.newBoatWidth()) > 0 &&
        positiveOrZero(this.newBoatDepth()) > 0,
    );

    readonly selectedBoat = this.facade.selectedBoat;

    readonly loadingBerths = signal<boolean>(false);
    readonly berthLoadError = signal<string | null>(null);

    readonly boatQuery = signal<string>('');

    // Berth list renders in two cases:
    //   - `direct` mode: always (user came in to pick a berth directly, or a
    //     new boat was applied — the boat acts as the width filter here).
    //   - `properties`: once the user has applied the form; the search
    //     dimensions act as the width filter and the list shows inline below
    //     the form. The new-boat form deliberately does NOT show
    //     an inline list — the user picks a berth via the `direct` mode after.
    readonly showBerthList = computed(() => {
        const mode = this.selectionMode();
        if (mode === 'direct') {
            return true;
        }
        if (mode === 'properties') {
            return this.activeSearchDims() !== null;
        }
        return false;
    });

    // What the berth list is fitted against: the active search, else the selected boat.
    private readonly fitDims = computed<BookingV2BoatDimensions | null>(() =>
        this.activeSearchDims() ?? this.selectedBoat(),
    );

    // Unfit toggle and fender only make sense with something to fit against.
    readonly hasFitBasis = computed<boolean>(() => this.fitDims() !== null);

    private readonly berthLoadTrigger = computed<BerthLoadRequest | null>(() => {
        if (!this.showBerthList()) {
            return null;
        }
        const { from, to, openEnd } = this.facade.dateRange();
        if (!from) {
            return null;
        }
        return { from, to: openEnd ? null : to };
    });

    private readonly availableBerths = toSignal(
        toObservable(this.berthLoadTrigger).pipe(
            switchMap(req => {
                if (!req) {
                    this.loadingBerths.set(false);
                    this.berthLoadError.set(null);
                    return of<readonly BookingV2BerthOption[]>([]);
                }
                this.loadingBerths.set(true);
                this.berthLoadError.set(null);
                return this.berthApi.getAll(this.buildBerthParams(req)).pipe(
                    map(list => list.map(toBerthOption)),
                    catchError(() => {
                        this.berthLoadError.set('bookingV2.berth.list.loadError');
                        return of<readonly BookingV2BerthOption[]>([]);
                    }),
                    finalize(() => this.loadingBerths.set(false)),
                );
            }),
        ),
        { initialValue: [] as readonly BookingV2BerthOption[] },
    );

    // `null` without a fit basis (no boat, no active search) — width filter is then disabled.
    readonly effectiveBoatWidth = computed<number | null>(() => {
        const dims = this.fitDims();
        return dims ? this.withFender(dims.width) : null;
    });

    // Berth must physically accommodate the boat — but only on dimensions the
    // berth actually has data for. A dimension is enforced solely when the
    // berth records a positive value for it; a missing/zero value (very common
    // for `maxDraft`, which many marinas never maintain per berth) means
    // "unknown, do not exclude" rather than "too small". Otherwise berths with
    // unmaintained depth/length data would silently vanish from every result.
    // Width additionally includes the optional fender add-on.
    // TODO(v2): push dimension filtering to the backend (`boatLength` /
    //   `boatWidth` / `boatDraft` as query params on `/berths`). Frontend
    //   filter is fine for typical marina sizes (≪ 500 berths) but does not
    //   scale, and ignores any backend-side restrictions (pier rules,
    //   maintenance bookings) the API may apply server-side.
    // Search-filtered berths only (no fit filter) — the basis for the partition below.
    private readonly searchedBerths = computed<readonly BookingV2BerthOption[]>(() => {
        const search = this.berthSearch().trim().toLowerCase();
        if (!search) {
            return this.availableBerths();
        }
        return this.availableBerths().filter(b =>
            b.handle.toLowerCase().includes(search)
            || (b.pierHandle?.toLowerCase().includes(search) ?? false),
        );
    });

    // Single fit-partition of the searched berths against the fit basis, each row
    // pre-tagged with its unfit reason. Computed once here so the list, the hidden
    // count and the per-row badge all read it — no repeated `berthUnfitReason` passes.
    // No fit basis → everything sits in `fitting` with a null reason, in unsorted API order.
    // While a search sits beside a real boat, each row is also checked against that boat, so
    // the row shows what the unfit-selection warning would say once it is picked.
    private readonly partitionedBerths = computed<{ fitting: BerthListItem[]; unfitting: BerthListItem[] }>(() => {
        const dims = this.fitDims();
        const searched = this.searchedBerths();
        if (!dims) {
            return { fitting: searched.map(berth => ({ berth, unfitReason: null, boatUnfitReason: null })), unfitting: [] };
        }
        const widthThreshold = this.effectiveBoatWidth();
        const boat = this.searchDimsBesideBoat() !== null ? this.selectedBoat() : null;
        const boatWidthThreshold = boat ? this.withFender(boat.width) : null;
        const fitting: BerthListItem[] = [];
        const unfitting: BerthListItem[] = [];
        for (const berth of searched) {
            const unfitReason = berthUnfitReason(berth, dims, widthThreshold);
            const boatUnfitReason = boat ? berthUnfitReason(berth, boat, boatWidthThreshold) : null;
            (unfitReason === null ? fitting : unfitting).push({ berth, unfitReason, boatUnfitReason });
        }
        // Fitting: tightest reserve first (6794). Unfitting: closest-to-fitting first
        // (least undersized on top) so the most usable fallback is the most prominent.
        fitting.sort((a, b) => compareBerthFit(a.berth, b.berth));
        unfitting.sort((a, b) => compareBerthUnfit(a.berth, b.berth));
        return { fitting, unfitting };
    });

    readonly filteredBerths = computed<readonly BerthListItem[]>(() => {
        const { fitting, unfitting } = this.partitionedBerths();
        // Unfit berths (6894) are appended — after all fitting ones — only once
        // revealed via the toggle, and only with a fit basis (boat or search). A Steg/berth
        // search auto-enables the toggle (onSearchChange, 6895) so the searched Steg's
        // full list shows, while the toggle stays available to restrict it again.
        if (!this.hasFitBasis() || !this.showUnfitting()) {
            return fitting;
        }
        return [...fitting, ...unfitting];
    });

    // Fitting (usable) matches — the primary berth count, independent of reveal.
    readonly fittingBerthCount = computed<number>(() => this.partitionedBerths().fitting.length);

    // Unfit matches currently appended to the list (only while revealed) — shown as a
    // separate tally so the header count is never conflated with usable berths.
    readonly shownUnfitCount = computed<number>(() =>
        this.hasFitBasis() && this.showUnfitting()
            ? this.partitionedBerths().unfitting.length
            : 0,
    );

    // Count of search-matching berths outside the fit basis — drives the hint that
    // offers to reveal them while they are still hidden (toggle off).
    readonly hiddenUnfitCount = computed<number>(() => {
        if (!this.hasFitBasis() || this.showUnfitting()) {
            return 0;
        }
        return this.partitionedBerths().unfitting.length;
    });

    // True when a *selected* berth does not fit the current boat — the toggle lets the
    // user pick such a berth deliberately, so this surfaces a non-blocking warning
    // rather than preventing the save. Checked against the boat itself, never the
    // search dimensions: the warning is about what actually gets booked. List rows mirror
    // it via `boatUnfitReason` while a search sits beside the boat.
    readonly hasUnfitSelection = computed<boolean>(() => {
        const boat = this.selectedBoat();
        if (!boat) {
            return false;
        }
        const widthThreshold = this.withFender(boat.width);
        return this.facade.berthOccupancies().some(o =>
            o.berth != null && berthUnfitReason(o.berth, boat, widthThreshold) !== null,
        );
    });

    readonly loadingBoatSearch = signal<boolean>(false);
    readonly importingBoatFromQr = signal<boolean>(false);
    readonly importingOwnerFromQr = signal<boolean>(false);

    readonly boatSearchResult = toSignal(
        toObservable(this.boatQuery).pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            map(q => q.trim()),
            distinctUntilChanged(),
            switchMap(q => {
                if (isUp2boatId(q)) {
                    this.loadingBoatSearch.set(true);
                    return from(this.up2boatIds.resolveBoat(q)).pipe(
                        switchMap(resolution => {
                            // Keine bekannte Up2Boat-ID (z. B. Bootsname): normale Suche.
                            if (!resolution.found) {
                                return this.searchBoats(q);
                            }
                            const boat = resolution.value;
                            if (boat?.id) {
                                this.selectBoat(toBoatOption(boat));
                                this.boatQuery.set('');
                            }
                            return of<BoatSearchResult>({ boats: [], owners: [], error: false });
                        }),
                        catchError(() => of<BoatSearchResult>({ boats: [], owners: [], error: false })),
                        finalize(() => this.loadingBoatSearch.set(false)),
                    );
                }
                if (q.length < MIN_SEARCH_LENGTH) {
                    this.loadingBoatSearch.set(false);
                    return of<BoatSearchResult>({ boats: [], owners: [], error: false });
                }
                this.loadingBoatSearch.set(true);
                return this.searchBoats(q);
            }),
        ),
        { initialValue: { boats: [], owners: [], error: false } satisfies BoatSearchResult },
    );

    private searchBoats(q: string): Observable<BoatSearchResult> {
        // PageParams only strips `filter`/`size`/`page`/`sort`/
        // `sortDirection`; anything else becomes a column filter.
        const baseParams = new HttpParams()
            .set('filter', q)
            .set('size', '30');
        const ownerParams = baseParams.set('includeBoats', 'true');
        // `startWith` per stream so one failing endpoint does not
        // swallow the results of the other two.
        const boats$ = this.boatApi.index(baseParams).pipe(
            map(page => (page?.results ?? []).map(toBoatOption)),
            startWith<readonly BookingV2BoatOption[]>([]),
            catchError(() => of<readonly BookingV2BoatOption[]>([])),
        );
        const persons$ = this.personApi.search(ownerParams).pipe(
            map(list => list.map(toPersonOwner)),
            startWith<readonly BookingV2OwnerOption[]>([]),
            catchError(() => of<readonly BookingV2OwnerOption[]>([])),
        );
        const companies$ = this.companyApi.index(ownerParams).pipe(
            map(page => (page?.results ?? []).map(toCompanyOwner)),
            startWith<readonly BookingV2OwnerOption[]>([]),
            catchError(() => of<readonly BookingV2OwnerOption[]>([])),
        );
        return combineLatest([boats$, persons$, companies$]).pipe(
            map(([boats, persons, companies]): BoatSearchResult => {
                // `/boats/index` already matches via the owner subquery,
                // so an owner-side match also appears in `boats`. Keep
                // owners as primary; strip their boats from the flat list.
                const allOwners = [...persons, ...companies];
                const ownerBoatIds = new Set(allOwners.flatMap(o => o.boats.map(b => b.id)));
                const boatsOnlyDirect = boats.filter(b => !ownerBoatIds.has(b.id));
                const owners = allOwners.filter(o => o.boats.length > 0);
                return { boats: boatsOnlyDirect, owners, error: false };
            }),
            finalize(() => this.loadingBoatSearch.set(false)),
        );
    }

    // Key is `${kind}-${id}`: person and company IDs share a number space.
    readonly expandedOwnerIds = signal<ReadonlySet<string>>(new Set());

    readonly boatQueryEmpty = computed(() => isBookingV2SearchTooShort(this.boatQuery()));

    readonly boatSearchHasResults = computed(() => {
        const r = this.boatSearchResult();
        return r.boats.length + r.owners.length > 0;
    });

    readonly boatShowNoResults = computed(() =>
        !this.loadingBoatSearch()
        && !this.boatQueryEmpty()
        && !this.boatSearchHasResults(),
    );

    // Owner picker for the new-boat form — persons + companies, regardless of
    // whether they already own a boat (unlike the existing-boat search).
    readonly ownerSearchResult = toSignal(
        toObservable(this.ownerQuery).pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            map(q => q.trim()),
            distinctUntilChanged(),
            switchMap(q => {
                if (isUp2boatId(q)) {
                    this.loadingOwnerSearch.set(true);
                    return from(this.up2boatIds.resolvePerson(q)).pipe(
                        switchMap(resolution => {
                            // Keine bekannte Up2Boat-ID (z. B. Nachname "Becker"): normale Suche.
                            if (!resolution.found) {
                                return this.searchOwners(q);
                            }
                            const person = resolution.value;
                            if (person?.id) {
                                this.selectNewBoatOwner(toPersonOwner(person));
                            }
                            return of<readonly BookingV2OwnerOption[]>([]);
                        }),
                        catchError(() => of<readonly BookingV2OwnerOption[]>([])),
                        finalize(() => this.loadingOwnerSearch.set(false)),
                    );
                }
                if (q.length < MIN_SEARCH_LENGTH) {
                    this.loadingOwnerSearch.set(false);
                    return of<readonly BookingV2OwnerOption[]>([]);
                }
                this.loadingOwnerSearch.set(true);
                return this.searchOwners(q);
            }),
        ),
        { initialValue: [] as readonly BookingV2OwnerOption[] },
    );

    private searchOwners(q: string): Observable<readonly BookingV2OwnerOption[]> {
        const params = new HttpParams()
            .set('filter', q)
            .set('size', '30');
        const persons$ = this.personApi.search(params).pipe(
            map(list => list.map(toPersonOwner)),
            startWith<readonly BookingV2OwnerOption[]>([]),
            catchError(() => of<readonly BookingV2OwnerOption[]>([])),
        );
        const companies$ = this.companyApi.index(params).pipe(
            map(page => (page?.results ?? []).map(toCompanyOwner)),
            startWith<readonly BookingV2OwnerOption[]>([]),
            catchError(() => of<readonly BookingV2OwnerOption[]>([])),
        );
        return combineLatest([persons$, companies$]).pipe(
            map(([persons, companies]): readonly BookingV2OwnerOption[] => [...persons, ...companies]),
            finalize(() => this.loadingOwnerSearch.set(false)),
        );
    }

    readonly ownerQueryEmpty = computed(() => isBookingV2SearchTooShort(this.ownerQuery()));

    readonly ownerSearchHasResults = computed(() => this.ownerSearchResult().length > 0);

    readonly ownerShowNoResults = computed(() =>
        !this.loadingOwnerSearch()
        && !this.ownerQueryEmpty()
        && !this.ownerSearchHasResults(),
    );

    constructor() {
        // Auto-expand multi-boat owners on every new result.
        effect(() => {
            const result = this.boatSearchResult();
            const keys = result.owners
                .filter(o => o.boats.length > 1)
                .map(o => this.ownerKey(o));
            this.expandedOwnerIds.set(new Set(keys));
        });

        // A placeholder boat (map prefill, or this step being re-entered) is an applied
        // properties search — restore it so the panel shows its values and its list.
        const boat = this.facade.selectedBoat();
        if (boat?.origin === 'manual') {
            this.searchDims.set({ length: boat.length, width: boat.width, depth: boat.depth });
            this.propLength.set(boat.length);
            this.propWidth.set(boat.width);
            this.propDepth.set(boat.depth);
        }
    }

    selectMode(mode: Exclude<BerthSelectionMode, null>): void {
        const next: BerthSelectionMode = this.selectionMode() === mode ? null : mode;
        this.selectionMode.set(next);
        // Reset only this mode's own search/form inputs — keep the already picked berth.
        // Nothing but the user drops a berth (6931): neither switching find-strategy nor
        // changing the boat wipes one they came in with, e.g. prefilled from the map.
        // An already-applied draft is kept too: its panel is the edit surface the user
        // returns to (mode tile or "Boot ändern") and applyNewBoatMode() collapses it
        // right after Übernehmen, so clearing here would let the next tile click silently
        // discard the typed boat. Only an unapplied draft is reset.
        const appliedOrigin = this.facade.selectedBoat()?.origin ?? null;
        // Snapshot the properties-search dimensions before the reset blocks below
        // clear them — used to seed the new-boat form on a properties→newBoat switch.
        const propDims = { length: this.propLength(), width: this.propWidth(), depth: this.propDepth() };
        // Clearing the berth search also ends the reveal it auto-enabled (6895), and a new
        // panel brings a new fit basis — same as onSearchChange('').
        this.berthSearch.set('');
        this.showUnfitting.set(false);
        if (next !== 'existingBoat') {
            this.boatQuery.set('');
            this.expandedOwnerIds.set(new Set());
        }
        // An applied search survives the switch (it re-applies when the panel reopens);
        // unapplied form input does not.
        if (next !== 'properties' && this.searchDims() === null) {
            this.resetPropFields();
        }
        if (next !== 'newBoat' && appliedOrigin !== 'new') {
            this.newBoatName.set('');
            this.newBoatLicense.set('');
            this.newBoatLength.set(null);
            this.newBoatWidth.set(null);
            this.newBoatDepth.set(null);
            this.newBoatOwner.set(null);
            this.ownerQuery.set('');
        } else {
            // Carry dimensions already entered via the properties search (or a
            // manual boat sitting in the selection) into the new-boat form, so
            // the user does not retype them. Only seed empty fields.
            this.seedNewBoatDimensions(propDims);
        }
        if (next === 'properties') {
            this.seedPropertiesFormFromContext();
        }
    }

    // Prefill new-boat length/width/depth from the last properties-search input
    // (or a selected manual boat). Existing non-empty values win.
    private seedNewBoatDimensions(propDims: { length: number | null; width: number | null; depth: number | null }): void {
        const manualBoat = this.selectedBoat()?.origin === 'manual' ? this.selectedBoat() : null;
        const length = propDims.length ?? manualBoat?.length ?? null;
        const width = propDims.width ?? manualBoat?.width ?? null;
        const depth = propDims.depth ?? manualBoat?.depth ?? null;
        if (this.newBoatLength() == null && length != null) this.newBoatLength.set(length);
        if (this.newBoatWidth() == null && width != null) this.newBoatWidth.set(width);
        if (this.newBoatDepth() == null && depth != null) this.newBoatDepth.set(depth);
    }

    /** #7242: after a real/new boat is selected, opening properties search should suggest its dimensions (still editable). */
    private seedPropertiesFormFromContext(): void {
        const applied = this.searchDims();
        if (applied) {
            if (this.propLength() == null) {
                this.propLength.set(applied.length);
            }
            if (this.propWidth() == null) {
                this.propWidth.set(applied.width);
            }
            if (this.propDepth() == null) {
                this.propDepth.set(applied.depth);
            }
            return;
        }
        const boat = this.selectedBoat();
        if (!boat || boat.origin === 'manual') {
            return;
        }
        if (this.propLength() == null && positiveOrZero(boat.length) > 0) {
            this.propLength.set(boat.length);
        }
        if (this.propWidth() == null && positiveOrZero(boat.width) > 0) {
            this.propWidth.set(boat.width);
        }
        if (this.propDepth() == null && positiveOrZero(boat.depth) > 0) {
            this.propDepth.set(boat.depth);
        }
    }

    // Picking a berth adds/removes an occupancy (Liegeplatzbelegung, 6609). A new
    // occupancy's window defaults to the booking date range and is not marked
    // overridden, so it keeps tracking the date step until the user expands it in
    // the summary and edits the window.
    toggleBerth(berth: BookingV2BerthOption): void {
        const current = this.facade.berthOccupancies();
        const existing = current.find(o => o.berth?.id === berth.id);
        if (this.multiSelect()) {
            this.facade.berthOccupancies.set(
                existing
                    ? current.filter(o => o.berth?.id !== berth.id)
                    : [...current, this.buildOccupancy(berth)],
            );
        } else {
            this.facade.berthOccupancies.set(existing ? [] : [this.buildOccupancy(berth)]);
        }
    }

    isBerthSelected(berth: BookingV2BerthOption): boolean {
        return this.facade.berthOccupancies().some(o => o.berth?.id === berth.id);
    }

    onMultiSelectChange(checked: boolean): void {
        this.multiSelect.set(checked);
        if (!checked && this.facade.berthOccupancies().length > 1) {
            this.facade.berthOccupancies.update(list => list.slice(0, 1));
        }
    }

    onShowUnfittingChange(checked: boolean): void {
        this.showUnfitting.set(checked);
    }

    private buildOccupancy(berth: BookingV2BerthOption): BookingV2BerthOccupancy {
        const { from, to, openEnd } = this.facade.dateRange();
        return { uuid: uuidv4(), berth, from, to, openEnd, overridden: false };
    }

    onSearchChange(value: string): void {
        const hadTerm = this.berthSearch().trim().length > 0;
        const hasTerm = value.trim().length > 0;
        this.berthSearch.set(value);
        // A Steg/berth search surfaces the searched Steg's full list including unfit
        // berths (6895): auto-reveal on the first search keystroke and hide them again
        // once the search is cleared — but leave the toggle in the user's control in
        // between, so they can restrict search results to fitting berths.
        if (!hadTerm && hasTerm) {
            this.showUnfitting.set(true);
        } else if (!hasTerm) {
            this.showUnfitting.set(false);
        }
    }

    onUseFenderChange(checked: boolean): void {
        this.useFender.set(checked);
    }

    onFenderCmChange(value: number | null): void {
        // Clamp + coerce to avoid NaN propagating into the width calc.
        this.fenderCm.set(Math.max(0, Math.min(100, Number(value) || 0)));
    }

    retryBerthLoad(): void {
        const currentMode = this.selectionMode();
        if (!this.showBerthList()) {
            return;
        }
        // Microtask, otherwise the computed dedups and `switchMap` never fires.
        this.selectionMode.set(null);
        queueMicrotask(() => this.selectionMode.set(currentMode));
    }

    onBoatQueryChange(value: string): void {
        this.boatQuery.set(value);
    }

    async scanBoatFromQr(): Promise<void> {
        if (this.importingBoatFromQr()) {
            return;
        }
        this.importingBoatFromQr.set(true);
        try {
            const boat = await this.up2boatIds.resolveBoatFromQr();
            if (boat?.id) {
                this.selectBoat(toBoatOption(boat));
                this.boatQuery.set('');
            }
        } finally {
            this.importingBoatFromQr.set(false);
        }
    }

    async scanOwnerFromQr(): Promise<void> {
        if (this.importingOwnerFromQr()) {
            return;
        }
        this.importingOwnerFromQr.set(true);
        try {
            const person = await this.up2boatIds.resolvePersonFromQr();
            if (person?.id) {
                this.selectNewBoatOwner(toPersonOwner(person));
                this.ownerQuery.set('');
            }
        } finally {
            this.importingOwnerFromQr.set(false);
        }
    }

    selectBoat(boat: BookingV2BoatOption): void {
        this.applyBoatSelection(boat);
        // A berth is already picked (e.g. prefilled from the map, or the user came
        // in to attach a boat to an existing berth): the selection is complete —
        // don't reopen the direct berth list, just show the ready state.
        if (this.facade.berthOccupancies().length > 0) {
            this.selectionMode.set(null);
            this.boatQuery.set('');
            this.expandedOwnerIds.set(new Set());
            // This path skips selectMode(), so reset the list state here too — fit is
            // boat-specific (6894/6895) and must not be carried over from the previous boat.
            this.berthSearch.set('');
            this.showUnfitting.set(false);
            return;
        }
        // Otherwise jump to the direct tab with the chosen boat acting as width filter.
        this.selectMode('direct');
    }

    // Boats nested under an owner search result lack an owner sub-graph, so the
    // owner identity is taken from the owner object and attached to the boat —
    // this is what lets the person step default to the owner later.
    selectOwnerBoat(owner: BookingV2OwnerOption, boat: BookingV2BoatOption): void {
        this.selectBoat({
            ...boat,
            ownerLabel: owner.fullName || boat.ownerLabel,
            ownerId: owner.id,
            ownerKind: owner.kind,
        });
    }

    onChangeBoatFromSummary(): void {
        const origin = this.selectedBoat()?.origin ?? 'existing';
        this.facade.selectedBoat.set(null);
        // The berth stays (6931) — removing the boat is not a berth change.
        // Return user to the panel they originally used.
        this.selectionMode.set(originToMode(origin));
        this.clearSearch();
        this.boatQuery.set('');
        this.expandedOwnerIds.set(new Set());
        // Fit is boat-specific — don't carry the reveal (or a search that would
        // auto-re-reveal it) into the next boat's list.
        this.berthSearch.set('');
        this.showUnfitting.set(false);
    }

    applyPropertiesMode(): void {
        if (!this.propertiesValid()) {
            return;
        }
        const dims: BookingV2BoatDimensions = {
            length: this.propLength() as number,
            width: this.propWidth() as number,
            depth: this.propDepth() as number,
        };
        // Without a real boat the dimensions also become the placeholder boat; a real or
        // new boat is never swapped for it (7165).
        const boat = this.selectedBoat();
        if (!boat || boat.origin === 'manual') {
            this.applyBoatSelection(toManualBoatOption(dims));
        }
        this.searchDims.set(dims);
        // New fit basis: unfit berths start hidden unless a berth search is active (6895).
        this.showUnfitting.set(this.berthSearch().trim().length > 0);
        // Stay on the properties panel — berth list renders below the form.
    }

    applyNewBoatMode(): void {
        if (!this.newBoatValid()) {
            return;
        }
        const owner = this.newBoatOwner();
        const boat: BookingV2BoatOption = {
            id: -2,
            name: this.newBoatName().trim() || null,
            licensePlate: this.newBoatLicense().trim() || null,
            length: this.newBoatLength() as number,
            width: this.newBoatWidth() as number,
            depth: this.newBoatDepth() as number,
            // Same as the manual case above: an unsaved boat has no persisted area yet.
            sqm: null,
            ownerLabel: owner?.fullName ?? null,
            ownerId: owner?.id ?? null,
            ownerKind: owner?.kind ?? null,
            origin: 'new',
        };
        this.applyBoatSelection(boat);
        // Collapse the panel: the boat now shows in the selection summary, and leaving a
        // filled duplicate form below it invites a second "Übernehmen" click (6931). The
        // inputs survive (selectMode() spares an applied draft), so reopening the panel —
        // directly or via "Boot ändern" — resumes it instead of starting from blank.
        this.selectionMode.set(null);
    }

    // Every boat-selection entry point (existing-boat search, properties,
    // new-boat) funnels through here.
    private applyBoatSelection(boat: BookingV2BoatOption): void {
        // Re-applying unchanged data is not a boat change (6931) — it would otherwise
        // trigger a berth reload and a dynamic-price re-evaluation for nothing.
        if (isSameBoatSelection(this.facade.selectedBoat(), boat)) {
            return;
        }
        this.facade.selectedBoat.set(boat);
        // A search applied for the previous boat must not silently filter for this one —
        // including a selection landing asynchronously (Up2Boat-ID/QR resolve).
        this.clearSearch();
        // The berth selection survives a boat change (6931): a berth the user picked —
        // or came in with, e.g. pre-seeded from a map tile click — is only theirs to
        // drop. A combination that no longer fits surfaces via hasUnfitSelection().
    }

    // Drops the applied properties search. Its form inputs go too unless the panel is open,
    // so a reopened panel never shows values that no longer filter anything.
    private clearSearch(): void {
        this.searchDims.set(null);
        if (this.selectionMode() !== 'properties') {
            this.resetPropFields();
        }
    }

    private resetPropFields(): void {
        this.propLength.set(null);
        this.propWidth.set(null);
        this.propDepth.set(null);
    }

    onOwnerQueryChange(value: string): void {
        this.ownerQuery.set(value);
    }

    selectNewBoatOwner(owner: BookingV2OwnerOption): void {
        this.newBoatOwner.set({ id: owner.id, kind: owner.kind, fullName: owner.fullName });
        this.ownerQuery.set('');
    }

    clearNewBoatOwner(): void {
        this.newBoatOwner.set(null);
        this.ownerQuery.set('');
    }

    onPropFieldChange(field: 'length' | 'width' | 'depth', raw: number | null): void {
        const v = clampDimension(raw);
        if (field === 'length') this.propLength.set(v);
        else if (field === 'width') this.propWidth.set(v);
        else this.propDepth.set(v);
    }

    onNewBoatFieldChange(field: 'length' | 'width' | 'depth', raw: number | null): void {
        const v = clampDimension(raw);
        if (field === 'length') this.newBoatLength.set(v);
        else if (field === 'width') this.newBoatWidth.set(v);
        else this.newBoatDepth.set(v);
    }

    ownerKey(owner: BookingV2OwnerOption): string {
        return `${owner.kind}-${owner.id}`;
    }

    toggleOwnerExpanded(owner: BookingV2OwnerOption): void {
        const key = this.ownerKey(owner);
        this.expandedOwnerIds.update(current => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    isOwnerExpanded(owner: BookingV2OwnerOption): boolean {
        return this.expandedOwnerIds().has(this.ownerKey(owner));
    }

    isBoatSelected(boat: BookingV2BoatOption): boolean {
        return this.selectedBoat()?.id === boat.id;
    }

    closeExistingBoatPanel(): void {
        this.selectionMode.set(null);
        this.boatQuery.set('');
        this.expandedOwnerIds.set(new Set());
    }

    private withFender(width: number): number {
        return width + (this.useFender() ? 2 * (this.fenderCm() / 100) : 0);
    }

    private buildBerthParams(req: BerthLoadRequest): HttpParams {
        let params = new HttpParams()
            .set('onlyFree', 'true')
            .set('from', toSqlDateTime(req.from));
        if (req.to) {
            params = params.set('to', toSqlDateTime(req.to));
        }
        return params;
    }
}

function positiveOrZero(v: number | null): number {
    return v == null || isNaN(v) || v < 0 ? 0 : v;
}

// Orders berths by length fit: smallest length first (tightest reserve for a given boat,
// since the caller only ever sorts within one fit class). Berths without a recorded length
// sink below all sized ones. Equal lengths break by natural handle order (12 before 103)
// for a deterministic, human-readable sequence. Length is the sole ordering dimension (LüA);
// width/draft stay filters only.
export type BerthUnfitReason = 'tooShort' | 'tooNarrow' | 'tooDeep';

// Why a berth cannot hold the boat, or `null` when it fits. A dimension is only
// enforced when the berth records a positive value for it (mirrors the filter in
// `filteredBerths`: a missing/zero value means "unknown, do not exclude"). Length is
// checked first so the primary dimension (LüA) wins when several constraints fail.
function berthUnfitReason(
    berth: BookingV2BerthOption,
    boat: BookingV2BoatDimensions,
    widthThreshold: number | null,
): BerthUnfitReason | null {
    if (berth.length > 0 && berth.length < boat.length) {
        return 'tooShort';
    }
    if (widthThreshold !== null && berth.width > 0 && berth.width < widthThreshold) {
        return 'tooNarrow';
    }
    if (berth.maxDraft > 0 && berth.maxDraft < boat.depth) {
        return 'tooDeep';
    }
    return null;
}

function compareBerthFit(a: BookingV2BerthOption, b: BookingV2BerthOption): number {
    const aKnown = a.length > 0;
    const bKnown = b.length > 0;
    if (aKnown !== bKnown) {
        return aKnown ? -1 : 1;
    }
    if (aKnown && a.length !== b.length) {
        return a.length - b.length;
    }
    return naturalCompare(a.handle, b.handle);
}

// Orders unfit berths closest-to-fitting first: the longest (least undersized) on top,
// so the most usable fallback is the most prominent — the reverse of the fitting order.
// Dimensionless berths sink to the end; equal lengths break by natural handle order.
function compareBerthUnfit(a: BookingV2BerthOption, b: BookingV2BerthOption): number {
    const aKnown = a.length > 0;
    const bKnown = b.length > 0;
    if (aKnown !== bKnown) {
        return aKnown ? -1 : 1;
    }
    if (aKnown && a.length !== b.length) {
        return b.length - a.length;
    }
    return naturalCompare(a.handle, b.handle);
}

// 0–99 m covers every plausible vessel; clamps NaN to null so the input
// clears rather than freezing on garbage values.
function clampDimension(raw: number | null): number | null {
    if (raw == null || isNaN(raw)) {
        return null;
    }
    return Math.max(0, Math.min(99, raw));
}

function originToMode(origin: BookingV2BoatOrigin): Exclude<BerthSelectionMode, null> {
    switch (origin) {
        case 'manual': return 'properties';
        case 'new': return 'newBoat';
        default: return 'existingBoat';
    }
}
